/**
 * Enum-like class representing notification frequency types for saved searches.
 * Provides predefined instances (`onPublishedListings` and `Never`) and utility methods for working with these types.
 */
export default class NotificationFrequencyType {

    /**
     * Gets the `onPublishedListings` notification frequency type.
     * Represents a notification frequency where notifications are sent on Published Listings.
     *
     * @returns {NotificationFrequencyType} The `onPublishedListings` instance.
     */
    static get OnPublishedListings() {  
        return this.getInstance('onPublishedListings');
    }

    /**
     * Gets the `Never` notification frequency type.
     * Represents a notification frequency where notifications are never sent.
     *
     * @returns {NotificationFrequencyType} The `Never` instance.
     */
    static get Never() {
        return this.getInstance('never');
    }

    /**
     * Retrieves a notification frequency type instance based on the provided key.
     *
     * @param {string} key - The key representing the notification frequency type
     * (e.g., 'onPublishedListings', 'never').
     * @returns {NotificationFrequencyType} The corresponding instance, or `undefined` if the key is invalid.
     */
    static fromKey(key) {
        const map = {
            onPublishedListings: NotificationFrequencyType.OnPublishedListings,
            never: NotificationFrequencyType.Never,
        };
        return map[key];
    }

    /**
     * Retrieves or creates an instance of `NotificationFrequencyType` for the given key.
     *
     * @param {string} key - The key representing the notification frequency type.
     * @returns {NotificationFrequencyType} The corresponding instance.
     */
    static getInstance(key) {
        if (!this.instances[key]) {
            this.instances[key] = new NotificationFrequencyType(key);
        }
        return this.instances[key];
    }

    /**
     * Creates a new `NotificationFrequencyType` instance.
     *
     * @param {string} name - The name of the notification frequency type.
     */
    constructor(name) {
        this.name = name;
    }

    /**
     * Converts the notification frequency type to its string representation.
     *
     * @returns {string} The string representation of the notification frequency type.
     */
    toString() {
        return `${this.name}`;
    }
}

// Stores instances of `NotificationFrequencyType` to ensure they are unique.
Object.defineProperty(NotificationFrequencyType, 'instances', { value: {} });